import React, { lazy, Suspense, useMemo, useState } from "react";
import { useUserDataFlattened } from "../../../modules/user/stores";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { ROUTES } from "@/types/routes";
import {
  useActivityPopupPriority,
  useIsActivityQualificationAutoEnabled,
  useIsHomeDisplayReady,
} from "@/modules/homeDisplay/hooks";
import { ActivityType, TimingType } from "@/modules/homeDisplay/types";
import { useHomeDisplayStore } from "@/modules/homeDisplay/stores";
import type { FirstRechargePopupResult } from "./useFirstRechargePopup.types";
import {
  qualifiesLegacyFirstRechargePopup,
  hideLegacyFirstRechargeForStaged,
} from "./firstRechargeFloatingHelpers";

const FirstRechargeBonus = lazy(() =>
  import("../components/FirstRechargeBonus/FirstRechargeBonus").then(m => ({
    default: m.FirstRechargeBonus,
  }))
);

const FirstRechargeBonusContent: React.FC<{ onComplete: () => void }> = ({
  onComplete,
}) => {
  const [visible, setVisible] = useState(true);

  const handleClose = () => {
    setVisible(false);
    onComplete();
  };

  return (
    <Suspense fallback={null}>
      <FirstRechargeBonus visible={visible} onClose={handleClose} />
    </Suspense>
  );
};

const FIRST_RECHARGE_CONFIG = POPUP_CONFIGS.FIRST_RECHARGE;

export const useFirstRechargePopup = (): FirstRechargePopupResult => {
  const { data: userInfo } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();
  const snapshot = useHomeDisplayStore(
    s => s.data?.firstRechargeStaged ?? null
  );
  const hideLegacyFirstRecharge = hideLegacyFirstRechargeForStaged(snapshot);

  const priority = useActivityPopupPriority(
    ActivityType.FIRST_DEPOSIT,
    FIRST_RECHARGE_CONFIG.priority,
    TimingType.ACTIVITY_QUALIFICATION
  );
  const isAutoEnabled = useIsActivityQualificationAutoEnabled(
    ActivityType.FIRST_DEPOSIT
  );

  const frbEndAt = useMemo(() => {
    if (typeof window === "undefined") return Date.now() + 60 * 60 * 1000;

    const now = Date.now();
    const key = `rechargeEndAt:${userInfo?.userId ?? "guest"}`;
    const savedRaw = localStorage.getItem(key);
    const saved = savedRaw ? Number(savedRaw) : 0;

    if (saved && saved > now) return saved;

    const newEndAt = now + 60 * 60 * 1000;
    localStorage.setItem(key, String(newEndAt));
    return newEndAt;
  }, [userInfo?.userId]);

  const shouldShow =
    !hideLegacyFirstRecharge &&
    qualifiesLegacyFirstRechargePopup(userInfo) &&
    Date.now() < frbEndAt;

  const { trigger } = useQueuedPopup({
    popupId: FIRST_RECHARGE_CONFIG.id,
    priority,
    shouldShow: () =>
      isConfigReady &&
      !hideLegacyFirstRecharge &&
      qualifiesLegacyFirstRechargePopup(userInfo) &&
      Date.now() < frbEndAt,
    renderContent: onComplete => (
      <FirstRechargeBonusContent onComplete={onComplete} />
    ),
    showOnce: true,
    allowManualTrigger: true,
    allowedPaths: [ROUTES.HOME, ROUTES.HOME_ALT, "/"],
    dependencies: [
      isConfigReady,
      hideLegacyFirstRecharge,
      snapshot?.firstDepositMode,
      snapshot?.showStagedUi,
      userInfo?.firstRechargeRuleAmount,
      userInfo?.firstRechargeFlag,
      userInfo?.firstRechargePopupShowPercent,
      userInfo?.firstRechargeRulePromoRatio,
    ],
    autoTrigger: isAutoEnabled,
    userBinding: true,
    getUserId: () => userInfo?.userId,
    timingType: TimingType.ACTIVITY_QUALIFICATION,
  });

  const remainingMs = Math.max(0, frbEndAt - Date.now());

  return {
    shouldShow,
    trigger,
    remainingMs,
  };
};
